/*
 * Google Ads tag (gtag.js), europe only.
 *
 * Loaded from here rather than as a plain <script> in index.html for two reasons:
 *
 *   - index.html is shared by both builds. A static tag would fire on ainovel.ru too, which
 *     already carries Yandex.Metrika and has no use for a Plotex conversion tag.
 *   - An advertising tag is the "marketing" category in utils/consent.js, and that module applies
 *     the strictest rule of the three jurisdictions to everyone: nothing non-essential runs until
 *     the visitor switches it on. Hard-coding the snippet into <head> would load it on the first
 *     paint, before the cookie banner has been answered — exactly what the Cookie Policy says does
 *     not happen, and the one thing the Russian-tracker guard in index.html was written to avoid.
 *
 * So the tag waits for consent, and starts the moment it is granted without needing a reload.
 * Revoking consent cannot unload a script that is already running; that takes a page load, which
 * is what the preference centre triggers anyway.
 */

import { IS_EUROPE } from '../config/region';
import { allows, onConsentChange } from './consent';
import { gtag } from './googleConsent';

const ADS_ID = 'AW-18444294870';

let started = false;
/* The URL the tag last reported, so a route change is sent once and the start page not twice. */
let lastUrl = null;

const startTag = () => {
  if (started) return;
  started = true;

  /* The consent default is already on the queue — initGoogleConsent runs before this. */
  gtag('js', new Date());
  gtag('config', ADS_ID);
  lastUrl = window.location.href;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${ADS_ID}`;
  document.head.appendChild(s);
};

/*
 * A page_view for a client-side route change.
 *
 * gtag('config') reports the page it was loaded on and nothing after it: this is a single-page app,
 * so moving from the catalogue to /payment never reloads and the tag never sees the new URL. The
 * "Просмотр оплата" conversion is a page-load rule on /payment, so without this it only counts
 * visitors who land on the payment page directly.
 *
 * The page the tag starts on is skipped — config has already reported it.
 */
export const trackAdsPageView = () => {
  if (!started) return;
  const url = window.location.href;
  if (url === lastUrl) return;
  lastUrl = url;
  gtag('event', 'page_view', { send_to: ADS_ID, page_location: url, page_title: document.title });
};

/*
 * Returns an unsubscribe function, or undefined when there is nothing to wait for.
 *
 * During prerendering there is no stored consent and no banner to answer, so allows() is false and
 * nothing is injected — the shipped HTML stays free of the tag, which is the right default for a
 * file a crawler reads.
 */
export const initGoogleAds = () => {
  if (!IS_EUROPE) return undefined;
  if (allows('marketing')) {
    startTag();
    return undefined;
  }
  return onConsentChange((consent) => {
    if (consent.marketing) startTag();
  });
};
